import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AutocompleteInput } from '../components/ui/autocomplete-input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ArrowLeft, Save, Loader2, Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { EMISSION_CATEGORIES, COUNTRIES, UNITS, getUnitCategory, getScopeFromCategory, ACTIVITY_CLASSIFICATION_OPTIONS } from '../lib/constants';
import { checkUkSpelling } from '../lib/ukSpelling';
import { toTitleCase } from '../lib/format';
import { useCategoryRules, checkCategoryRules } from '../lib/categoryRules';
import { TagAndActivityFields, ContinentField } from '../components/CategoryRuleFields';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The schema depends on the category rules (Activity Classification / Tag restrictions), which come from the backend
const buildSchema = (rules) => z.object({
  category: z.string().min(1, 'Category is required'),
  activity_classification: z.string().min(1, 'Activity Classification is required').refine(
    (val) => ACTIVITY_CLASSIFICATION_OPTIONS.includes(val),
    { message: 'Please select a valid Activity Classification' }
  ),
  sub_category: z.string().trim().min(1, 'Sub category is required'),
  type: z.string().trim().optional().nullable(),
  sub_type: z.string().trim().optional().nullable(),
  country: z.string().min(1, 'Country is required'),
  continent: z.string().optional().nullable(),
  state: z.string().trim().min(1, "State is required - enter '*' if this factor applies to every state"),
  city: z.string().trim().min(1, "City is required - enter '*' if this factor applies to every city"),
  input_unit: z.string().min(1, 'Input unit is required'),
  unit_category: z.string().optional().nullable(),
  source_name: z.string().min(1, 'Source name is required'),
  source_version: z.string().min(1, 'Source version (year) is required'),
  scope: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  site_identity: z.string().optional().nullable(),
  ar_version: z.string().optional().nullable(),
  tag: z.string().optional().nullable(),
  co2e: z.coerce.number().min(0, 'CO2e cannot be negative').optional().nullable(),
  co2factor: z.coerce.number().min(0, 'CO2 Factor cannot be negative').optional().nullable(),
  ch4factor: z.coerce.number().min(0, 'CH4 Factor cannot be negative').optional().nullable(),
  n2o_factor: z.coerce.number().min(0, 'N2O Factor cannot be negative').optional().nullable(),
  no2factor: z.coerce.number().min(0, 'NO2 Factor cannot be negative').optional().nullable(),
  co4factor: z.coerce.number().min(0, 'CO4 Factor cannot be negative').optional().nullable(),
  biogenic_co2factor: z.coerce.number().min(0, 'Biogenic CO2 Factor cannot be negative').optional().nullable(),
  sourceurl: z.string().min(1, 'Source URL is required'),
  water_use_m3: z.coerce.number().min(0, 'Water use cannot be negative').optional().nullable(),
  water_data_source_name: z.string().optional().nullable(),
  water_data_source_version: z.string().optional().nullable(),
  water_data_sourceurl: z.string().optional().nullable(),
  flag_co2e: z.coerce.number().min(0, 'Flag CO2e cannot be negative').optional().nullable(),
  flag_source_name: z.string().optional().nullable(),
  flag_source_version: z.string().optional().nullable(),
  flag_sourceurl: z.string().optional().nullable(),
  additional_information: z.string().optional().nullable(),
  is_primary_factor: z.boolean().optional().nullable(),
  is_spend_based: z.boolean().optional().nullable(),
  is_plastic: z.string().optional().nullable(),
  plastic_category: z.string().optional().nullable(),
}).refine((data) => {
  // Validate fiber subcategory - Water data mandatory
  if (data.sub_category && data.sub_category.toLowerCase() === 'fiber') {
    if (!data.water_use_m3) return false;
    if (!data.water_data_source_name) return false;
    if (!data.water_data_source_version) return false;
    if (!data.water_data_sourceurl) return false;
  }
  return true;
}, {
  message: "Water data fields are required when sub-category is 'Fiber'",
  path: ["water_use_m3"]
}).refine((data) => {
  // Validate fiber subcategory - Flag data mandatory
  if (data.sub_category && data.sub_category.toLowerCase() === 'fiber') {
    if (!data.flag_co2e) return false;
    if (!data.flag_source_name) return false;
    if (!data.flag_source_version) return false;
    if (!data.flag_sourceurl) return false;
  }
  return true;
}, {
  message: "Flag data fields are required when sub-category is 'Fiber'",
  path: ["flag_co2e"]
}).superRefine((data, ctx) => checkCategoryRules(rules, data, ctx));

// Generate year options (current year back to 1990)
const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear - 1989 }, (_, i) => currentYear - i);

// Scope options
const SCOPE_OPTIONS = ['1', '2', '3'];

// Guidance shown for the Additional Information field
const ADDITIONAL_INFO_GUIDANCE = [
  {
    title: 'Proxy Used',
    desc: 'Describe what dataset/process was used as a substitute for the actual activity/material and why it is appropriate.',
  },
  {
    title: 'Proxy Justification Source',
    desc: 'Source supporting the use of this proxy, including where to find the relevant information in it — e.g., page number, section, table. Include URL where applicable.',
  },
  {
    title: 'Key Assumptions',
    desc: 'List any assumptions made — e.g., weight per unit, density, regional proxy, GSM, EOL treatment split.',
  },
  {
    title: 'Assumption Sources',
    desc: 'Source for each assumption, including where to find it — e.g., sheet name, page, section. Include URLs where applicable.',
  },
  {
    title: 'Dataset / Process Name & Location in Source',
    desc: 'Exact name of the dataset/process as it appears in the main EF source, and where to find the EF value — e.g., sheet name, page number, table, column header.',
  },
  {
    title: 'Additional Notes / Caveats',
    desc: 'Any limitations of the proxy, scope inclusions/exclusions, or data quality flags.',
  },
];

const FormField = ({ name, label, type = 'text', placeholder, required = false, register, errors }) => (
  <div className="space-y-2">
    <Label htmlFor={name}>
      {label}
      {required && <span className="text-destructive ml-1">*</span>}
    </Label>
    <Input
      id={name}
      type={type}
      step={type === 'number' ? 'any' : undefined}
      min={type === 'number' ? '0' : undefined}
      placeholder={placeholder}
      className={`input-eco ${errors[name] ? 'border-red-500 ring-1 ring-red-500' : ''}`}
      {...register(name)}
      data-testid={`input-${name}`}
    />
    {errors[name] && (
      <p className="text-sm text-destructive">{errors[name].message}</p>
    )}
  </div>
);

// Classification field with DB-backed suggestions + non-blocking UK-spelling hint
const ClassificationField = ({ name, label, required = false, placeholder, suggestions, control, setValue, errors }) => {
  const [ukSuggestion, setUkSuggestion] = useState(null);
  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <AutocompleteInput
            value={field.value}
            onChange={(v) => { field.onChange(v); setUkSuggestion(null); }}
            onBlur={() => {
              const raw = typeof field.value === 'string' ? field.value.trim() : '';
              if (!raw) { setUkSuggestion(null); return; }
              const normalized = toTitleCase(raw);
              if (normalized !== field.value) field.onChange(normalized);
              setUkSuggestion(checkUkSpelling(normalized).suggestion);
            }}
            suggestions={suggestions}
            placeholder={placeholder}
            className={`input-eco ${errors[name] ? 'border-red-500 ring-1 ring-red-500' : ''}`}
            testId={`input-${name}`}
          />
        )}
      />
      {errors[name] && <p className="text-sm text-destructive">{errors[name].message}</p>}
      {ukSuggestion && (
        <p className="text-xs text-amber-600" data-testid={`uk-hint-${name}`}>
          UK spelling:{' '}
          <button
            type="button"
            className="underline font-medium hover:text-amber-700"
            onClick={() => { setValue(name, ukSuggestion, { shouldValidate: true }); setUkSuggestion(null); }}
          >
            Did you mean “{ukSuggestion}”?
          </button>
        </p>
      )}
    </div>
  );
};

export default function EmissionFactorFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);

  const { rules, continents, country_continents: countryContinents } = useCategoryRules();
  const schema = useMemo(() => buildSchema(rules), [rules]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      is_primary_factor: false,
      is_spend_based: false,
      is_plastic: '',
      country: '',
      category: '',
      activity_classification: '',
      input_unit: '',
      unit_category: '',
    }
  });

  const isPrimaryFactor = watch('is_primary_factor');
  const isSpendBased = watch('is_spend_based');
  const selectedUnit = watch('input_unit');
  const siteIdentity = watch('site_identity');
  const unitCategory = watch('unit_category');
  const subCategory = watch('sub_category');
  const selectedCategory = watch('category');
  const selectedType = watch('type');

  // Cascading suggestion lists (user may still enter free-text values)
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [subTypeOptions, setSubTypeOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);

  // Check if subcategory is fiber (case-insensitive)
  const isFiberSubCategory = subCategory && subCategory.toLowerCase() === 'fiber';

  // Suggest sub-categories based on the selected category
  useEffect(() => {
    const fetchSubCategories = async () => {
      try {
        const params = new URLSearchParams();
        if (selectedCategory) params.append('category', selectedCategory);
        const response = await axios.get(`${API_URL}/emission-factors/sub-categories/list?${params}`);
        setSubCategoryOptions(response.data || []);
      } catch (error) {
        console.error('Failed to fetch sub-category suggestions:', error);
        setSubCategoryOptions([]);
      }
    };
    fetchSubCategories();
  }, [selectedCategory]);

  // Suggest types based on category + sub-category
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const params = new URLSearchParams();
        if (selectedCategory) params.append('category', selectedCategory);
        if (subCategory) params.append('sub_category', subCategory);
        const response = await axios.get(`${API_URL}/emission-factors/types/list?${params}`);
        setTypeOptions(response.data || []);
      } catch (error) {
        console.error('Failed to fetch type suggestions:', error);
        setTypeOptions([]);
      }
    };
    fetchTypes();
  }, [selectedCategory, subCategory]);

  // Suggest sub-types based on category + sub-category + type
  useEffect(() => {
    const fetchSubTypes = async () => {
      try {
        const params = new URLSearchParams();
        if (selectedCategory) params.append('category', selectedCategory);
        if (subCategory) params.append('sub_category', subCategory);
        if (selectedType) params.append('type', selectedType);
        const response = await axios.get(`${API_URL}/emission-factors/sub-types/list?${params}`);
        setSubTypeOptions(response.data || []);
      } catch (error) {
        console.error('Failed to fetch sub-type suggestions:', error);
        setSubTypeOptions([]);
      }
    };
    fetchSubTypes();
  }, [selectedCategory, subCategory, selectedType]);

  // Suggest tags based on the selected category
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const params = new URLSearchParams();
        if (selectedCategory) params.append('category', selectedCategory);
        const response = await axios.get(`${API_URL}/emission-factors/tags/list?${params}`);
        setTagOptions(response.data || []);
      } catch (error) {
        console.error('Failed to fetch tag suggestions:', error);
        setTagOptions([]);
      }
    };
    fetchTags();
  }, [selectedCategory]);

  // Auto-populate unit category when unit changes
  useEffect(() => {
    if (selectedUnit) {
      const category = getUnitCategory(selectedUnit);
      setValue('unit_category', category);
    }
  }, [selectedUnit, setValue]);

  // Auto-set is_spend_based based on unit category (locked field)
  useEffect(() => {
    if (unitCategory === 'CURRENCY') {
      setValue('is_spend_based', true);
    } else {
      setValue('is_spend_based', false);
    }
  }, [unitCategory, setValue]);

  // Auto-set is_primary_factor based on site_identity (locked field)
  useEffect(() => {
    if (siteIdentity && siteIdentity.trim() !== '') {
      setValue('is_primary_factor', true);
    } else {
      setValue('is_primary_factor', false);
    }
  }, [siteIdentity, setValue]);

  // Auto-set scope based on category
  useEffect(() => {
    if (selectedCategory) {
      const scope = getScopeFromCategory(selectedCategory);
      if (scope) {
        setValue('scope', scope);
      }
    }
  }, [selectedCategory, setValue]);

  const fetchFactor = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/emission-factors/${id}`);
      const data = response.data;
      // Reset form with fetched data
      Object.keys(data).forEach(key => {
        if (key !== 'id' && key !== 'emission_factor_id' && key !== 'status' && key !== 'created_at' && key !== 'created_by' && key !== 'updated_at' && key !== 'approved_at' && key !== 'approved_by' && key !== 'version') {
          setValue(key, data[key]);
        }
      });
    } catch (error) {
      toast.error('Failed to load emission factor');
      navigate('/emission-factors');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, setValue]);

  useEffect(() => {
    if (isEdit) {
      fetchFactor();
    }
  }, [isEdit, fetchFactor]);

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const cleanedData = { ...data };

      // Normalize classification fields to Title Case
      ['sub_category', 'type', 'sub_type'].forEach(field => {
        if (typeof cleanedData[field] === 'string' && cleanedData[field].trim()) {
          cleanedData[field] = toTitleCase(cleanedData[field].trim());
        }
      });

      // Clean numeric fields
      const numericFields = ['co2e', 'co2factor', 'ch4factor', 'n2o_factor', 'no2factor', 'co4factor', 'biogenic_co2factor', 'water_use_m3', 'flag_co2e'];
      numericFields.forEach(field => {
        if (cleanedData[field] === '' || cleanedData[field] === undefined) {
          cleanedData[field] = null;
        }
      });
      
      // Clean optional string fields
      const optionalStringFields = ['type', 'sub_type', 'state', 'city', 'unit_category', 'scope', 'description', 
        'site_identity', 'ar_version', 'tag', 'water_data_source_name', 'water_data_source_version', 
        'water_data_sourceurl', 'flag_source_name', 'flag_source_version', 'flag_sourceurl', 'additional_information',
        'is_plastic', 'plastic_category', 'continent'];
      optionalStringFields.forEach(field => {
        if (cleanedData[field] === '' || cleanedData[field] === undefined) {
          cleanedData[field] = null;
        }
      });

      if (isEdit) {
        await axios.put(`${API_URL}/emission-factors/${id}`, cleanedData);
        toast.success('Emission factor updated successfully');
      } else {
        await axios.post(`${API_URL}/emission-factors`, cleanedData);
        toast.success('Emission factor created successfully');
      }
      navigate('/emission-factors');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save emission factor');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="ef-form-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/emission-factors')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold">
              {isEdit ? 'Edit Emission Factor' : 'New Emission Factor'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isEdit ? 'Update the emission factor details' : 'Create a new emission factor for approval'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <Accordion type="multiple" defaultValue={['general', 'location', 'emission']} className="space-y-4">
            {/* General Information */}
            <AccordionItem value="general" className="border border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="font-heading font-semibold text-lg py-4">
                General Information
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Category Dropdown */}
                  <div className="space-y-2">
                    <Label>Category <span className="text-destructive">*</span></Label>
                    <Controller
                      name="category"
                      control={control}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger className="input-eco" data-testid="select-category">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {EMISSION_CATEGORIES.map(cat => (
                              <SelectItem key={cat.value} value={cat.label}>{cat.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.category && <p className="text-sm text-destructive">{errors.category.message}</p>}
                  </div>

                  {/* Sub Category / Type / Sub Type - DB-backed suggestions + UK-spelling hint (free entry allowed) */}
                  <ClassificationField
                    name="sub_category"
                    label="Sub Category"
                    required
                    placeholder="Select or type... e.g., Electricity"
                    suggestions={subCategoryOptions}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                  />
                  <ClassificationField
                    name="type"
                    label="Type"
                    placeholder="Select or type... e.g., Grid"
                    suggestions={typeOptions}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                  />
                  <ClassificationField
                    name="sub_type"
                    label="Sub Type"
                    placeholder="Select or type... e.g., National"
                    suggestions={subTypeOptions}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                  />
                  
                  {/* Scope Dropdown - Auto-selected based on Category */}
                  <div className="space-y-2">
                    <Label>Scope <span className="text-muted-foreground text-xs">(auto-selected)</span></Label>
                    <Controller
                      name="scope"
                      control={control}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value} disabled>
                          <SelectTrigger className="input-eco bg-gray-100" data-testid="select-scope">
                            <SelectValue placeholder="Auto-selected based on category" />
                          </SelectTrigger>
                          <SelectContent>
                            {SCOPE_OPTIONS.map(scope => (
                              <SelectItem key={scope} value={scope}>Scope {scope}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    <p className="text-xs text-muted-foreground">Scope is automatically set based on the selected category</p>
                  </div>
                  
                  {/* Tag + Activity Classification: restricted by the category's rules */}
                  <TagAndActivityFields
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    getValues={getValues}
                    rules={rules}
                    renderFreeTag={(field) => (
                      <AutocompleteInput
                        value={field.value}
                        onChange={field.onChange}
                        suggestions={tagOptions}
                        placeholder="Select or type... e.g., renewable"
                        className={`input-eco ${errors.tag ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                        testId="input-tag"
                      />
                    )}
                  />
                  
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Enter description..."
                      className="input-eco min-h-[100px]"
                      {...register('description')}
                      data-testid="input-description"
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Location */}
            <AccordionItem value="location" className="border border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="font-heading font-semibold text-lg py-4">
                Location
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Continent (optional) */}
                  <ContinentField control={control} setValue={setValue} continents={continents} countryContinents={countryContinents} />

                  {/* Country Dropdown */}
                  <div className="space-y-2">
                    <Label>Country <span className="text-destructive">*</span></Label>
                    <Controller
                      name="country"
                      control={control}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger className="input-eco" data-testid="select-country">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {COUNTRIES.map(country => (
                              <SelectItem key={country.code} value={country.code}>{country.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.country && <p className="text-sm text-destructive">{errors.country.message}</p>}
                  </div>
                  
                  <FormField name="state" label="State" placeholder="e.g., California" register={register} errors={errors} />
                  <FormField name="city" label="City" placeholder="e.g., San Francisco" register={register} errors={errors} />
                  <FormField name="site_identity" label="Site Identity" placeholder="e.g., Site-001" register={register} errors={errors} />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Emission Data */}
            <AccordionItem value="emission" className="border border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="font-heading font-semibold text-lg py-4">
                Emission Data
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* CO2e first, then CO2 */}
                  <FormField name="co2e" label="CO2e" type="number" placeholder="0.0" register={register} errors={errors} />
                  <FormField name="co2factor" label="CO2 Factor" type="number" placeholder="0.0" register={register} errors={errors} />
                  <FormField name="ch4factor" label="CH4 Factor" type="number" placeholder="0.0" register={register} errors={errors} />
                  <FormField name="n2o_factor" label="N2O Factor" type="number" placeholder="0.0" register={register} errors={errors} />
                  <FormField name="no2factor" label="NO2 Factor" type="number" placeholder="0.0" register={register} errors={errors} />
                  <FormField name="co4factor" label="CO4 Factor" type="number" placeholder="0.0" register={register} errors={errors} />
                  <FormField name="biogenic_co2factor" label="Biogenic CO2 Factor" type="number" placeholder="0.0" register={register} errors={errors} />
                  
                  {/* Input Unit Dropdown */}
                  <div className="space-y-2">
                    <Label>Input Unit <span className="text-destructive">*</span></Label>
                    <Controller
                      name="input_unit"
                      control={control}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger className="input-eco" data-testid="select-input-unit">
                            <SelectValue placeholder="Select unit" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {UNITS.map(u => (
                              <SelectItem key={u.unit} value={u.unit}>{u.unit}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.input_unit && <p className="text-sm text-destructive">{errors.input_unit.message}</p>}
                  </div>
                  
                  {/* Unit Category - Auto-populated, read-only */}
                  <div className="space-y-2">
                    <Label>Unit Category (auto)</Label>
                    <Input
                      {...register('unit_category')}
                      className="input-eco bg-gray-100"
                      readOnly
                      placeholder="Auto-populated"
                      data-testid="input-unit_category"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className={`flex items-center justify-between p-4 rounded-lg ${siteIdentity && siteIdentity.trim() ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                    <div>
                      <Label htmlFor="is_primary_factor">Is Primary Factor <span className="text-muted-foreground text-xs">(auto)</span></Label>
                      <p className="text-sm text-muted-foreground">
                        {siteIdentity && siteIdentity.trim() ? 'Enabled because Site Identity is set' : 'Add Site Identity to enable'}
                      </p>
                    </div>
                    <Switch
                      id="is_primary_factor"
                      checked={isPrimaryFactor}
                      disabled
                      data-testid="switch-is_primary_factor"
                    />
                  </div>
                  <div className={`flex items-center justify-between p-4 rounded-lg ${unitCategory === 'CURRENCY' ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                    <div>
                      <Label htmlFor="is_spend_based">Is Spend Based <span className="text-muted-foreground text-xs">(auto)</span></Label>
                      <p className="text-sm text-muted-foreground">
                        {unitCategory === 'CURRENCY' ? 'Enabled because Unit Category is Currency' : 'Select a Currency unit to enable'}
                      </p>
                    </div>
                    <Switch
                      id="is_spend_based"
                      checked={isSpendBased}
                      disabled
                      data-testid="switch-is_spend_based"
                    />
                  </div>
                  
                  {/* Plastic Fields */}
                  <div className="space-y-2">
                    <Label>Is Plastic</Label>
                    <Controller
                      name="is_plastic"
                      control={control}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value || ''}>
                          <SelectTrigger className="input-eco" data-testid="select-is_plastic">
                            <SelectValue placeholder="Select Yes/No" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Yes">Yes</SelectItem>
                            <SelectItem value="No">No</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Plastic Category</Label>
                    <Controller
                      name="plastic_category"
                      control={control}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value || ''}>
                          <SelectTrigger className="input-eco" data-testid="select-plastic_category">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Category 1">Category 1</SelectItem>
                            <SelectItem value="Category 2">Category 2</SelectItem>
                            <SelectItem value="Category 3">Category 3</SelectItem>
                            <SelectItem value="Category 4">Category 4</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Source Information */}
            <AccordionItem value="source" className="border border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="font-heading font-semibold text-lg py-4">
                Source Information
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField name="source_name" label="Source Name" placeholder="e.g., EPA" required register={register} errors={errors} />
                  
                  {/* Source Version - Year Dropdown */}
                  <div className="space-y-2">
                    <Label>Source Version (Year) <span className="text-destructive">*</span></Label>
                    <Controller
                      name="source_version"
                      control={control}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger className="input-eco" data-testid="select-source-version">
                            <SelectValue placeholder="Select year" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {YEAR_OPTIONS.map(year => (
                              <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.source_version && <p className="text-sm text-destructive">{errors.source_version.message}</p>}
                  </div>
                  
                  <FormField name="ar_version" label="AR Version" placeholder="e.g., AR6" register={register} errors={errors} />
                  <div className="md:col-span-2">
                    <FormField name="sourceurl" label="Source URL" placeholder="https://..." required register={register} errors={errors} />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Water Data */}
            <AccordionItem value="water" className="border border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="font-heading font-semibold text-lg py-4">
                Water Data
                {isFiberSubCategory && <span className="ml-2 text-sm text-destructive font-normal">(Required for Fiber)</span>}
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                {isFiberSubCategory && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    ⚠️ All water data fields are mandatory when sub-category is &quot;Fiber&quot;
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="water_use_m3">
                      Water Use (m³)
                      {isFiberSubCategory && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <Input
                      id="water_use_m3"
                      type="number"
                      step="any"
                      placeholder="0.0"
                      className="input-eco"
                      {...register('water_use_m3')}
                      data-testid="input-water_use_m3"
                    />
                    {errors.water_use_m3 && <p className="text-sm text-destructive">{errors.water_use_m3.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="water_data_source_name">
                      Water Data Source
                      {isFiberSubCategory && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <Input
                      id="water_data_source_name"
                      placeholder="e.g., WRI"
                      className="input-eco"
                      {...register('water_data_source_name')}
                      data-testid="input-water_data_source_name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="water_data_source_version">
                      Water Source Version
                      {isFiberSubCategory && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <Input
                      id="water_data_source_version"
                      placeholder="e.g., 2024"
                      className="input-eco"
                      {...register('water_data_source_version')}
                      data-testid="input-water_data_source_version"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="water_data_sourceurl">
                      Water Source URL
                      {isFiberSubCategory && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <Input
                      id="water_data_sourceurl"
                      placeholder="https://..."
                      className="input-eco"
                      {...register('water_data_sourceurl')}
                      data-testid="input-water_data_sourceurl"
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Flag Data */}
            <AccordionItem value="flag" className="border border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="font-heading font-semibold text-lg py-4">
                Flag Data
                {isFiberSubCategory && <span className="ml-2 text-sm text-destructive font-normal">(Required for Fiber)</span>}
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                {isFiberSubCategory && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    ⚠️ All flag data fields are mandatory when sub-category is &quot;Fiber&quot;
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="flag_co2e">
                      Flag CO2e
                      {isFiberSubCategory && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <Input
                      id="flag_co2e"
                      type="number"
                      step="any"
                      placeholder="0.0"
                      className="input-eco"
                      {...register('flag_co2e')}
                      data-testid="input-flag_co2e"
                    />
                    {errors.flag_co2e && <p className="text-sm text-destructive">{errors.flag_co2e.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="flag_source_name">
                      Flag Source Name
                      {isFiberSubCategory && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <Input
                      id="flag_source_name"
                      placeholder="e.g., IPCC"
                      className="input-eco"
                      {...register('flag_source_name')}
                      data-testid="input-flag_source_name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="flag_source_version">
                      Flag Source Version
                      {isFiberSubCategory && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <Input
                      id="flag_source_version"
                      placeholder="e.g., 2024"
                      className="input-eco"
                      {...register('flag_source_version')}
                      data-testid="input-flag_source_version"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="flag_sourceurl">
                      Flag Source URL
                      {isFiberSubCategory && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <Input
                      id="flag_sourceurl"
                      placeholder="https://..."
                      className="input-eco"
                      {...register('flag_sourceurl')}
                      data-testid="input-flag_sourceurl"
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Additional Information */}
            <AccordionItem value="additional" className="border border-gray-200 rounded-lg px-6">
              <AccordionTrigger className="font-heading font-semibold text-lg py-4">
                Additional Information
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="additional_information">Additional Information</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-primary transition-colors"
                          aria-label="What to include in Additional Information"
                          data-testid="additional-info-guidance"
                        >
                          <Info className="w-4 h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-[440px] max-h-[420px] overflow-y-auto">
                        <p className="font-heading font-semibold text-sm mb-3">What to include</p>
                        <ol className="list-decimal pl-4 space-y-3 text-sm text-muted-foreground">
                          {ADDITIONAL_INFO_GUIDANCE.map((g, i) => (
                            <li key={i}>
                              <span className="font-medium text-foreground">{g.title}</span>
                              <span className="block mt-0.5">{g.desc}</span>
                            </li>
                          ))}
                        </ol>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <Textarea
                    id="additional_information"
                    placeholder="Proxy used, proxy justification source, key assumptions, assumption sources, dataset/process name & location in source, additional notes/caveats. Click the ⓘ icon for details."
                    className="input-eco min-h-[150px]"
                    {...register('additional_information')}
                    data-testid="input-additional_information"
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Submit Button */}
          <div className="flex items-center justify-end gap-4 pt-4">
            <Button type="button" variant="outline" onClick={() => navigate('/emission-factors')} data-testid="cancel-btn">
              Cancel
            </Button>
            <Button type="submit" className="btn-primary gap-2" disabled={submitting} data-testid="submit-btn">
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEdit ? 'Update' : 'Create'} Emission Factor
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
